import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { navigateTo } from '../../common/navigation/url';
import ActivationErrorModal from '../../common/components/activation/ActivationErrorModal';
import ActivationDuplicateErrorModal from '../../common/components/activation/ActivationDuplicateErrorModal';
import TrialActivationSuccessContainer from './TrialActivationSuccessContainer';
import { activateTrial } from './activateTrial';
import { track } from '../../common/eventTracking/tracker';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { getTrialGuideUrl, getTrialGuideUrlFromApiName } from '../../utils/links';
import { refresh } from '../../utils/links';
import { trialIdsWithTrialGuide, upgradeProductToTrialIds, apiNameToTrialIds } from 'self-service-api/constants/Trials';
import Raven from 'raven-js';
import { trackFailedApiNameMap, trackTrialActivation } from '../../utils/metricUtils';
const statuses = {
  LOADING: 'loading',
  ERROR: 'error',
  SUCCESS: 'success'
};
const HIDE_SUBTITLE_SCREENS = ['products-plans', 'service-hub-paywall'];

const TrialActivationModalContainer = props => {
  const [trialActivationRequestStatus, setTrialActivationRequestStatus] = useState(statuses.LOADING);
  const [showTrialLoadingState, setShowTrialLoadingState] = useState(!!props.shouldHideSurvey);
  const [error, setError] = useState(null);
  const isMounted = useRef(false);

  const {
    activeTrials,
    expiredTrials,
    isMultiTrial,
    hideBackground = false,
    upgradeData,
    upgradeData: {
      apiName,
      app,
      screen,
      uniqueId,
      upgradeProduct
    },
    onSuccess,
    shouldHideSurvey,
    canViewNewSeats,
    isTX0120Variant = false,
    isTX0120Fetched = false,
    isEligibleForTX0120 = false
  } = props,
        successModalPassthroughProps = _objectWithoutPropertiesLoose(props, ["activeTrials", "expiredTrials", "isMultiTrial", "hideBackground", "upgradeData", "upgradeData", "onSuccess", "shouldHideSurvey", "canViewNewSeats", "isTX0120Variant", "isTX0120Fetched", "isEligibleForTX0120"]);

  const showSubtitle = !HIDE_SUBTITLE_SCREENS.includes(screen);
  const defaultOnSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      const eligibleForNewSeats = canViewNewSeats && apiName;

      const getTrialId = () => {
        if (eligibleForNewSeats && apiNameToTrialIds[apiName]) {
          return apiNameToTrialIds[apiName];
        } else {
          if (eligibleForNewSeats) {
            trackFailedApiNameMap('getTrialId', apiName, upgradeProduct);
            Raven.captureMessage(`failed to get trial id from apiName ${apiName} in getTrialId`);
          }

          return upgradeProductToTrialIds[upgradeProduct];
        }
      };

      const trialId = getTrialId();

      if (eligibleForNewSeats && trialId && trialIdsWithTrialGuide.includes(trialId)) {
        navigateTo(getTrialGuideUrlFromApiName(apiName, `${app}-${screen}-${uniqueId}`));
      } else if (trialId && trialIdsWithTrialGuide.includes(trialId)) {
        navigateTo(getTrialGuideUrl(upgradeProduct, `${app}-${screen}-${uniqueId}`));
      } else {
        refresh();
      }
    }
  }, [onSuccess, app, screen, uniqueId, upgradeProduct, apiName, canViewNewSeats]);
  useEffect(() => {
    isMounted.current = true;
    track('trialModalInteraction', Object.assign({
      action: 'opened modal container'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    activateTrial({
      upgradeData,
      isMultiTrial,
      activeTrials,
      expiredTrials
    }).then(() => {
      if (isMounted.current) {
        setTrialActivationRequestStatus(statuses.SUCCESS);
        setShowTrialLoadingState(false);
        trackTrialActivation(upgradeData.app, upgradeData.upgradeProduct);
      }
    }).catch(err => {
      if (isMounted.current) {
        setTrialActivationRequestStatus(statuses.ERROR);
        setError(err);
      }
    });
    return () => {
      isMounted.current = false;
    };
  }, [activeTrials, expiredTrials, isMultiTrial, upgradeData]);

  switch (trialActivationRequestStatus) {
    case statuses.LOADING:
      return null;

    case statuses.ERROR:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation error modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));

      if (error && error.responseJSON) {
        const activateError = error.responseJSON;

        if (activateError.message && activateError.message.match(/^Cannot start duplicate trial/)) {
          return /*#__PURE__*/_jsx(ActivationDuplicateErrorModal, {
            onSuccess: defaultOnSuccess,
            upgradeData: upgradeData
          });
        }
      }

      return /*#__PURE__*/_jsx(ActivationErrorModal, {});

    case statuses.SUCCESS:
      track('trialModalInteraction', Object.assign({
        action: 'opening activation success modal'
      }, getUpgradeDataTrackingProperties(upgradeData), {
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));
      return /*#__PURE__*/_jsx(TrialActivationSuccessContainer, Object.assign({
        onSuccess: defaultOnSuccess,
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        isMultiTrial: isMultiTrial,
        hideBackground: hideBackground,
        upgradeData: upgradeData,
        showTrialLoadingState: showTrialLoadingState,
        showSubtitle: showSubtitle,
        shouldHideSurvey: shouldHideSurvey,
        canViewNewSeats: canViewNewSeats,
        isTX0120Variant: isTX0120Variant,
        isTX0120Fetched: isTX0120Fetched,
        isEligibleForTX0120: isEligibleForTX0120
      }, successModalPassthroughProps));

    default:
      return null;
  }
};

export default TrialActivationModalContainer;